<template>
  <FormGroup
    :id="id"
    :label="label"
    :help-text="helpText"
    :invalid-feedback="invalidFeedback ?? errorMessage"
    :required="required"
    :valid-feedback="validFeedback"
  >
    <div class="dropdown">
      <div ref="triggerEl" class="input-group mb-3">
        <input
          v-bind="$attrs"
          type="text"
          class="form-control"
          :id="id"
          :name="name"
          :value="value"
          :required="required"
          @input="onInput"
          @focus="onFocus"
        />

        <div class="input-group-append">
          <UIButton
            type="button"
            variant="secondary"
            :disabled="loading || !value?.length"
            @click="onSearch"
          >
            {{
              loading
                ? $t("form.field.search.button.loadingLabel")
                : $t("form.field.search.button.label")
            }}
          </UIButton>
        </div>
      </div>

      <div
        v-if="isVisible"
        ref="listEl"
        class="dropdown-menu show shadow-sm"
        style="width: 100%; max-height: 200px; overflow-y: auto"
        :style="style"
      >
        <button
          v-for="option in options"
          type="button"
          class="dropdown-item"
          :key="getValue(option)"
          @click="onSelect(option)"
        >
          {{ getLabel(option) }}
        </button>

        <div v-if="loading" class="text-center">
          <UISpinner />
        </div>

        <em v-else-if="!options.length" class="dropdown-item-text">
          No option found - please enter manually.
        </em>
      </div>
    </div>
  </FormGroup>
</template>

<script lang="ts">
export type Option = string | Record<string, unknown>;

export type SearchFieldProps = FormGroupProps & {
  labelKey?: string;
  loading?: boolean;
  modelValue?: string | null;
  name?: string;
  options: Option[];
  rules?: StringSchema;
  valueKey?: string;
};

export type SearchFieldEvents = {
  (event: "search"): unknown;
  (event: "select", option: Option): unknown;
  (event: "update:modelValue", value: string | null): unknown;
};
</script>

<script lang="ts" setup>
import UISpinner from "@/components/UI/UISpinner.vue";
import UIButton from "@/components/UI/UIButton.vue";
import { useDropdown } from "@/hooks/useDropdown";
import { uniqueId } from "underscore";
import { RuleExpression, useField } from "vee-validate";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { StringSchema, string } from "yup";
import FormGroup, { FormGroupProps } from "./FormGroup.vue";

const props = withDefaults(defineProps<SearchFieldProps>(), {
  id: uniqueId("field-"),
  labelKey: "label",
  valueKey: "value",
});
const emit = defineEmits<SearchFieldEvents>();
const { t } = useI18n();

const listEl = ref<HTMLElement>();
const triggerEl = ref<HTMLElement>();
const { isVisible, style, setVisible } = useDropdown(triggerEl, listEl);

const rules = computed<RuleExpression<string | null | undefined>>(() => {
  let schema = string().nullable();

  if (props.rules) {
    schema = schema.concat(props.rules);
  }
  if (props.required) {
    schema = schema.required(t("form.field.search.invalid.required"));
  }

  return schema;
});

const { value, errorMessage } = useField<string | null | undefined>(
  () => props.name ?? props.id,
  rules,
  {
    syncVModel: true,
  }
);

function onInput(event: Event) {
  emit("update:modelValue", (<HTMLInputElement>event.target).value);

  if (typeof props.modelValue === "undefined") {
    value.value = (<HTMLInputElement>event.target).value;
  }
}

function onFocus(event: FocusEvent) {
  if (props.options.length) {
    setVisible(true, event);
  }
}

function onSearch(event: Event) {
  setVisible(true, event);
  emit("search");
}

function onSelect(option: Option) {
  emit("select", option);

  if (typeof props.modelValue === "undefined") {
    value.value = getValue(option);
  }
}

function getLabel(option: Option): string {
  return String(typeof option !== "object" ? option : option[props.labelKey]);
}

function getValue(option: Option): string {
  return typeof option !== "object" ? option : <string>option[props.valueKey];
}
</script>
